import { FlightsSelect } from "./FlightsSelect/FlightsSelect";
import { useFlightResults } from "../../../hooks/useFlightResults/useFlightResults";
import { FieldProps } from "@/core/components/Field";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import { useFormContext } from "react-hook-form";
import { useEffect } from "react";
import { flightMapper } from "@/services/FlightMapper/FlightMapper";
import { BannerProps } from "@/core/types/types";
import PartialFlowHeader from "./FlightPickerHeaders/PartialFlowHeader";
import DelayFlowHeader from "./FlightPickerHeaders/DelayFlowHeader";

const OUTPUT_VARIABLE_NAME: string = "selectedFlights";

const FlightPicker = ({ field }: FieldProps) => {
  const {
    properties: { requiredInputVariables, group },
  } = field;
  const showDelayFlowBanner: boolean = group === "true";

  if (!requiredInputVariables) {
    throw new Error("Missing required input variable names.");
  }
  const { result } = useGetPreviousAnswers(requiredInputVariables);
  const {
    departureDate,
    originIATA: originAirportData,
    destinationIATA: destinationAirportData,
  } = result;

  const {
    selectedFlightDate,
    selectedFlight,
    flightResults,
    setSelectedFlightDate,
    setSelectedFlight,
    hasError,
  } = useFlightResults({
    departureDate,
    originAirport: originAirportData?.iata,
    destinationAirport: destinationAirportData?.iata,
  });

  const {
    setValue,
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  // TODO: Remove one of the possible banners after v0.1 -> v1 migration
  const bannerProps: BannerProps = {
    originAirportData,
    destinationAirportData,
    selectedFlightDate,
    setSelectedFlightDate,
  };

  // Save value to the form
  useEffect(() => {
    const newValue = selectedFlight
      ? [flightMapper.fromSearchResult(selectedFlight)]
      : null;

    setValue(OUTPUT_VARIABLE_NAME, newValue);
    setValue(field.key, selectedFlight);
    clearErrors(field.key);
  }, [selectedFlight]);

  useEffect(() => {
    setSelectedFlight(flightResults.length ? undefined : null);
    clearErrors(field.key);
  }, [flightResults]);

  if (hasError) {
    // TODO: Loading spinner?
    return null;
  }

  if (
    !selectedFlightDate ||
    !originAirportData ||
    !destinationAirportData ||
    !flightResults
  )
    return null;

  return (
    <>
      {showDelayFlowBanner ? (
        <DelayFlowHeader {...bannerProps} />
      ) : (
        <PartialFlowHeader {...bannerProps} />
      )}

      <FlightsSelect
        field={field}
        control={control}
        errors={errors}
        flights={flightResults}
        selectedFlight={selectedFlight}
        onFlightSelect={setSelectedFlight}
      />
    </>
  );
};

export { FlightPicker };
